import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { shopApi } from "@/shop/shopApi";

const CART_KEY = "sh_cart_id";
const EMAIL_KEY = "sh_recovery_email";
const NAME_KEY = "sh_recovery_name";
const CartContext = createContext(null);

function toSnapshot(cart) {
  if (!cart) return null;
  return {
    checkout_url: cart.checkout_url || "",
    subtotal: cart.subtotal || cart.total || null,
    items: (cart.lines || []).map((l) => ({
      title: l.product_title || "Item",
      image: l.image || null,
      price: l.price || null,
      quantity: l.quantity || 1,
      product_handle: l.product_handle || null,
      variant_title: l.variant_title || null,
    })),
  };
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [email, setEmailState] = useState(() => localStorage.getItem(EMAIL_KEY) || "");
  const [name, setNameState] = useState(() => localStorage.getItem(NAME_KEY) || "");
  const trackTimer = useRef(null);

  const setEmail = useCallback((v) => {
    setEmailState(v);
    if (v) localStorage.setItem(EMAIL_KEY, v);
    else localStorage.removeItem(EMAIL_KEY);
  }, []);

  const setName = useCallback((v) => {
    setNameState(v || "");
    if (v) localStorage.setItem(NAME_KEY, v);
    else localStorage.removeItem(NAME_KEY);
  }, []);

  // Restore cart on mount
  useEffect(() => {
    const id = localStorage.getItem(CART_KEY);
    if (id) {
      shopApi.cartGet(id).then(setCart).catch(() => {
        localStorage.removeItem(CART_KEY);
      });
    }
  }, []);

  // Debounced tracking when cart + email are present
  const scheduleTrack = useCallback((nextCart, nextEmail, nextName) => {
    if (trackTimer.current) clearTimeout(trackTimer.current);
    if (!nextCart?.id || !nextEmail || !nextCart?.lines?.length) return;
    trackTimer.current = setTimeout(() => {
      const snap = toSnapshot(nextCart);
      shopApi.cartTrack({
        cart_id: nextCart.id,
        email: nextEmail,
        name: nextName || "",
        checkout_url: snap.checkout_url,
        subtotal: snap.subtotal,
        items: snap.items,
      }).catch(() => {});
    }, 500);
  }, []);

  useEffect(() => {
    scheduleTrack(cart, email, name);
  }, [cart, email, name, scheduleTrack]);

  const addItem = useCallback(async (variantId, quantity = 1) => {
    setBusy(true);
    try {
      const existingId = localStorage.getItem(CART_KEY);
      let next;
      if (existingId && cart) {
        next = await shopApi.cartAdd(existingId, [{ variant_id: variantId, quantity }]);
      } else {
        next = await shopApi.cartCreate([{ variant_id: variantId, quantity }]);
        localStorage.setItem(CART_KEY, next.id);
      }
      setCart(next);
      setOpen(true);
      return next;
    } finally {
      setBusy(false);
    }
  }, [cart]);

  const updateItem = useCallback(async (lineId, quantity) => {
    const id = localStorage.getItem(CART_KEY);
    if (!id) return;
    setBusy(true);
    try {
      const next = await shopApi.cartUpdate(id, lineId, quantity);
      setCart(next);
      if (!next.total_quantity) {
        localStorage.removeItem(CART_KEY);
        setCart(null);
      }
    } finally {
      setBusy(false);
    }
  }, []);

  const beginCheckout = useCallback(async () => {
    if (!cart?.checkout_url) return;
    if (cart?.id) {
      shopApi.cartComplete(cart.id).catch(() => {});
    }
    window.location.href = cart.checkout_url;
  }, [cart]);

  const count = cart?.total_quantity || 0;

  return (
    <CartContext.Provider value={{
      cart, count, open, setOpen, addItem, updateItem, busy,
      email, setEmail, name, setName, beginCheckout,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
